import { FIELD_TYPES, FILE_FIELD_TYPES } from "@hyro-components/fields/utils";
import { FILTER_OPERATORS_DATA } from "@hyro-components/hyro-filter/HyroFilter.consts";
import dayjs from "dayjs";

function cleanString(value) {
  return value?.toString().toLowerCase().trim();
}

function cleanValue(value, type, overrideType, context) {
  switch (type) {
    case FIELD_TYPES.TEXT:
    case FIELD_TYPES.PHONE:
    case FIELD_TYPES.URL:
    case FIELD_TYPES.INT:
      return cleanString(value);

    case FIELD_TYPES.MULTI_LANGUAGE_TEXT:
      // Multi languguage field with single language should be treated as text field
      if (overrideType === FIELD_TYPES.TEXT) {
        return cleanString(value[context.defaultLanguage]);
      }
      return Object.values(value)?.map(item => cleanString(item));

    case FIELD_TYPES.ACTION:
      return JSON.stringify(value);

    case FIELD_TYPES.LIST:
      return value?.map(item => cleanString(item))?.join(" ");

    case FIELD_TYPES.FILE:
      return value;

    default:
      return cleanString(value);
  }
}

function matchesFilter(row, filterItem, context) {
  if (!filterItem.value || !filterItem.value.length) {
    return true;
  }

  const rowValue = cleanValue(row[filterItem.key], filterItem.type, filterItem.overrideType, context);
  const type = filterItem.overrideType || filterItem.parentType || filterItem.type;

  switch (type) {
    case FIELD_TYPES.INT:
    case FIELD_TYPES.TEXT:
    case FIELD_TYPES.PHONE:
    case FIELD_TYPES.URL:
    case FIELD_TYPES.MULTI_LANGUAGE_TEXT:
      return filterItem.value.some(value => {
        const cleanFilterValue = cleanValue(value);
        const operator = filterItem.operator || FILTER_OPERATORS_DATA.IS.key;
        const valueArray = typeof rowValue === "string" ? [rowValue] : rowValue;

        switch (operator) {
          case FILTER_OPERATORS_DATA.IS.key:
            return valueArray?.some(item => item === cleanFilterValue);
          case FILTER_OPERATORS_DATA.IS_NOT.key:
            return !valueArray?.some(item => item === cleanFilterValue);
          case FILTER_OPERATORS_DATA.CONTAINS.key:
            return valueArray?.some(item => item?.includes(cleanFilterValue));
          case FILTER_OPERATORS_DATA.NOT_CONTAINS.key:
            return !valueArray?.some(item => item?.includes(cleanFilterValue));
          case FILTER_OPERATORS_DATA.STARTS_WITH.key:
            return valueArray?.some(item => item?.startsWith(cleanFilterValue));
          case FILTER_OPERATORS_DATA.ENDS_WITH.key:
            return valueArray?.some(item => item?.endsWith(cleanFilterValue));
          default:
            return valueArray?.some(item => item?.includes(cleanFilterValue));
        }
      });

    case FIELD_TYPES.ACTION:
    case FIELD_TYPES.LIST:
      return filterItem.value.some(value => rowValue?.includes(cleanValue(value)));

    case FIELD_TYPES.BOOL:
    case FIELD_TYPES.CHOOSE_ONE:
    case FIELD_TYPES.COLLECTION_REF:
    case FIELD_TYPES.CHOOSE_AT_LEAST_ONE: {
      const rawValue = row[filterItem.key];
      // Array-valued columns (e.g. multi-target "Applies to"): match if ANY element equals a selected option.
      if (Array.isArray(rawValue)) {
        return filterItem.value.some(value =>
          rawValue.some(item =>
            cleanString(item) === cleanValue(value?.display_key) ||
            cleanString(item) === cleanValue(value?.key)));
      }
      return filterItem.value.some(value =>
        rowValue === cleanValue(value?.display_key) ||
          rowValue === cleanValue(value?.key)
      );
    }

    case FIELD_TYPES.FILE:
      if (filterItem.key.includes(FILE_FIELD_TYPES.UPLOAD_DATE)) {
        return filterItem.value.some(str => (dayjs(parseInt(rowValue)).format("MMMM D, YYYY")).includes(str));
      }
      return filterItem.value.some(value => rowValue.includes(cleanValue(value)));

    case FIELD_TYPES.DATE: {
      if (!rowValue || !Array.isArray(filterItem.value) || filterItem.value.length !== 2) {
        return false;
      }

      const rowDate = dayjs(rowValue);
      const startDate = dayjs(filterItem.value[0]).startOf("day");
      const endDate = dayjs(filterItem.value[1]).endOf("day");

      return rowDate.isValid() && rowDate.isBetween(startDate, endDate);
    }

    default:
      return false;
  }
}

function matchesSearch(row, searchText, searchFields) {
  if (!row || !searchText) return false;

  const values = searchFields?.length ? searchFields.map(field => row[field]) : Object.values(row);

  return values.some(value => {
    if (!value) return false;

    if (typeof value === "string") {
      return value.toLowerCase().includes(searchText.toLowerCase());
    }

    if (Array.isArray(value)) {
      return value.some(item =>
        typeof item === "string" && item.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    if (typeof value === "object") {
      return Object.values(value).some(item =>
        typeof item === "string" && item.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    return false;
  });
}

export { cleanString, cleanValue, matchesFilter, matchesSearch };
